import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { InventoryService } from '../services/inventoryService';
import { Inventory, InventoryCategory, Warehouse } from 'public-contracts';
import { InventoryQRManager } from './components/InventoryQRManager';
import { InventoryQRScannerDialog } from './components/InventoryQRScannerDialog';
import { InventoryStockUpdateDialog } from './components/InventoryStockUpdateDialog';
import { inventoryQRPrintService } from './services/inventoryQRPrintService';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';

// Lucide React icons
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Package,
  Warehouse as WarehouseIcon,
  Tag,
  AlertTriangle,
  QrCode,
  Printer,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface InventoryListProps {
  onCreateClick: () => void;
  onEditClick: (inventory: Inventory) => void;
  refreshTrigger?: number; // Add refreshTrigger prop
}

export const ModernInventoryList: React.FC<InventoryListProps> = ({
  onCreateClick,
  onEditClick,
  refreshTrigger,
}) => {
  const { translate } = useTranslation();
  const [inventories, setInventories] = useState<Inventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inventoryToDelete, setInventoryToDelete] = useState<Inventory | null>(
    null
  );
  const [qrManagerOpen, setQrManagerOpen] = useState(false);
  const [inventoryForQR, setInventoryForQR] = useState<Inventory | null>(null);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [stockUpdateDialogOpen, setStockUpdateDialogOpen] = useState(false);
  const [scannedInventory, setScannedInventory] = useState<Inventory | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const loadInventories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await InventoryService.getInventories({
        page: currentPage,
        size: pageSize,
        search: searchTerm || undefined,
        warehouseId:
          selectedWarehouse && selectedWarehouse !== 'all'
            ? selectedWarehouse
            : undefined,
        categoryId:
          selectedCategory && selectedCategory !== 'all'
            ? selectedCategory
            : undefined,
      });
      setInventories(response.data || []);
      setTotalItems(response.total || 0);
      console.log(`✅ Loaded ${response.data?.length || 0} inventory items (total: ${response.total})`);
      // Log warehouse IDs from inventories to help debug
      if (response.data && response.data.length > 0) {
        const warehouseIds = Array.from(
          new Set(response.data.map((inv: any) => inv.warehouseId).filter(Boolean))
        );
        console.log('📦 Inventory warehouse IDs:', warehouseIds);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load inventories';
      console.error('❌ Failed to load inventories:', err);
      setError(errorMessage);
      setInventories([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedWarehouse, selectedCategory, currentPage, pageSize]);

  const handleQRScanSuccess = useCallback(
    async (inventoryData: any) => {
      console.log('QR scan successful:', inventoryData);
      setQrScannerOpen(false);

      try {
        // Fetch the full inventory data
        const scannedInv = await InventoryService.getInventory(inventoryData.inventoryId);
        setScannedInventory(scannedInv);
        setStockUpdateDialogOpen(true);
      } catch (error) {
        console.error('Failed to load inventory:', error);
        // Fallback: just refresh the list
        loadInventories();
      }
    },
    [loadInventories]
  );

  const handleStockUpdateSuccess = useCallback(() => {
    setStockUpdateDialogOpen(false);
    setScannedInventory(null);
    // Refresh the inventory list to show updated data
    loadInventories();
  }, [loadInventories]);

  const handleQRScanError = useCallback((error?: string) => {
    console.error('QR scan error:', error);
    setQrScannerOpen(false);
  }, []);

  const handlePrintQRCode = useCallback(async (inventory: Inventory) => {
    try {
      await inventoryQRPrintService.printQRCode(
        inventory.id,
        inventory.code,
        inventory.name,
        {
          size: 200,
          includeLabel: true,
          includeCode: true,
          includeName: true,
          labelText: 'Inventory QR Code',
        }
      );
    } catch (error) {
      console.error('Failed to print QR code:', error);
      // You could add a toast notification here
    }
  }, []);

  const loadWarehouses = useCallback(async () => {
    try {
      const warehouses = await InventoryService.getWarehouses();
      const normalizedWarehouses = warehouses && Array.isArray(warehouses) 
        ? warehouses.map((w: any) => ({
            ...w,
            id: w.id || w._id || (w.id ? String(w.id) : ''),
          }))
        : [];
      console.log(
        '✅ Loaded warehouses for list:',
        normalizedWarehouses.length
      );
      console.log(
        '📦 Warehouse IDs:',
        normalizedWarehouses.map((w: any) => ({
          id: w.id,
          _id: w._id,
          name: w.name,
        }))
      );
      setWarehouses(normalizedWarehouses);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
      setWarehouses([]);
    }
  }, []);

  useEffect(() => {
    loadInventories();
    loadWarehouses();
    loadCategories();
  }, [loadInventories, loadWarehouses, refreshTrigger]); // Reload when refreshTrigger changes

  const loadCategories = async () => {
    try {
      const categories = await InventoryService.getInventoryCategories();
      setCategories(categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleDelete = async () => {
    if (!inventoryToDelete) return;

    try {
      await InventoryService.deleteInventory(inventoryToDelete.id);
      setInventories((prev) =>
        prev.filter((inv) => inv.id !== inventoryToDelete.id)
      );
      setDeleteDialogOpen(false);
      setInventoryToDelete(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete inventory'
      );
    }
  };

  const handleFilter = () => {
    setCurrentPage(1); // Reset to first page when filtering
    loadInventories();
  };

  const getWarehouseName = (warehouseId: string) => {
    if (!warehouseId) return 'No Warehouse';

    // Try exact match first with id
    let warehouse = warehouses && Array.isArray(warehouses) 
      ? warehouses.find((w: any) => {
          const wId = w.id || w._id;
          return wId === warehouseId || String(wId) === String(warehouseId);
        })
      : null;

    if (!warehouse) {
      console.warn('❌ Warehouse not found:', {
        lookingFor: warehouseId,
        type: typeof warehouseId,
        availableWarehouses: warehouses.map((w: any) => ({
          id: w.id,
          _id: w._id,
          idType: typeof w.id,
          _idType: typeof w._id,
          name: w.name,
          normalizedId: w.id || w._id,
        })),
        warehousesCount: warehouses.length,
      });
    }

    return warehouse?.name || 'Unknown Warehouse';
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || 'Unknown Category';
  };

  const getStockStatus = (quantity: number) => {
    if (quantity === 0)
      return { label: 'Out of Stock', color: 'error' as const };
    if (quantity < 10) return { label: 'Low Stock', color: 'warning' as const };
    return { label: 'In Stock', color: 'success' as const };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Package className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">
            {translate('pos.inventory.name', 'Inventory Management')}
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => setQrScannerOpen(true)}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <QrCode className="h-4 w-4" />
            <span>{translate('pos.inventory.scanQR', 'Scan QR Code')}</span>
          </Button>
          <Button
            onClick={onCreateClick}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>{translate('pos.inventory.add', 'Add Inventory')}</span>
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>{translate('common.filters', 'Filters')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">
                {translate('pos.inventory.search', 'Search')}
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder={translate(
                    'pos.inventory.search',
                    'Search inventories...'
                  )}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="warehouse">
                {translate('pos.inventory.warehouse.name', 'Warehouse')}
              </Label>
              <Select
                value={selectedWarehouse}
                onValueChange={setSelectedWarehouse}
              >
                <SelectTrigger>
                  <SelectValue placeholder={translate('common.all', 'All')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {translate('common.all', 'All')}
                  </SelectItem>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">
                {translate('pos.inventory.category.name', 'Category')}
              </Label>
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger>
                  <SelectValue placeholder={translate('common.all', 'All')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {translate('common.all', 'All')}
                  </SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button
                variant="outline"
                onClick={handleFilter}
                className="w-full flex items-center space-x-2"
              >
                <Filter className="h-4 w-4" />
                <span>{translate('common.filter', 'Filter')}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Inventory Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {translate('pos.inventory.list', 'Inventory Items')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{translate('pos.inventory.code', 'Code')}</TableHead>
                <TableHead>{translate('pos.inventory.name', 'Name')}</TableHead>
                <TableHead>
                  {translate('pos.inventory.quantity', 'Quantity')}
                </TableHead>
                <TableHead>
                  {translate('pos.inventory.price', 'Price')}
                </TableHead>
                <TableHead>
                  {translate('pos.inventory.warehouse.name', 'Warehouse')}
                </TableHead>
                <TableHead>
                  {translate('pos.inventory.category.name', 'Category')}
                </TableHead>
                <TableHead>
                  {translate('pos.inventory.status', 'Status')}
                </TableHead>
                <TableHead className="text-right">
                  {translate('common.actions', 'Actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inventories && inventories.length > 0 ? (
                inventories.map((inventory) => {
                  const stockStatus = getStockStatus(inventory.quantity);
                  return (
                    <TableRow key={inventory.id || inventory.code}>
                    <TableCell className="font-medium">
                      {inventory.code}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{inventory.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {inventory.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{inventory.quantity.toLocaleString()}</TableCell>
                    <TableCell>
                      {inventory.totalPrice.toLocaleString()}{' '}
                      {inventory.currency}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <WarehouseIcon className="h-4 w-4 text-muted-foreground" />
                        <span>{getWarehouseName(inventory.warehouseId)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span>
                          {getCategoryName(inventory.inventoryCategoryId)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          stockStatus.color === 'error'
                            ? 'destructive'
                            : stockStatus.color === 'warning'
                            ? 'secondary'
                            : 'default'
                        }
                      >
                        {stockStatus.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setInventoryForQR(inventory);
                            setQrManagerOpen(true);
                          }}
                          data-testid={`qr-button-${inventory.code}`}
                          title={translate('pos.inventory.qrCode', 'QR Code')}
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePrintQRCode(inventory)}
                          data-testid={`print-button-${inventory.code}`}
                          title={translate(
                            'pos.inventory.printQR',
                            'Print QR Code'
                          )}
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditClick(inventory)}
                          data-testid={`edit-button-${inventory.code}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setInventoryToDelete(inventory);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-button-${inventory.code}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                  {translate('pos.inventory.noItemsFound', 'No inventory items found')}
                </TableCell>
              </TableRow>
            )}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-muted-foreground">
                {translate('common.showing', 'Showing')}{' '}
                <span className="font-medium">
                  {Math.min(totalItems, (currentPage - 1) * pageSize + 1)}
                </span>{' '}
                {translate('common.to', 'to')}{' '}
                <span className="font-medium">
                  {Math.min(totalItems, currentPage * pageSize)}
                </span>{' '}
                {translate('common.of', 'of')}{' '}
                <span className="font-medium">{totalItems}</span>{' '}
                {translate('common.results', 'results')}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="ml-1">{translate('common.previous', 'Previous')}</span>
                </Button>
                <div className="flex items-center justify-center min-w-[32px] text-sm font-medium">
                  {currentPage} / {Math.ceil(totalItems / pageSize)}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(Math.ceil(totalItems / pageSize), prev + 1))}
                  disabled={currentPage >= Math.ceil(totalItems / pageSize)}
                >
                  <span className="mr-1">{translate('common.next', 'Next')}</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate('pos.inventory.deleteTitle', 'Delete Inventory')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'inventory.delete.message',
                'Are you sure you want to delete this inventory item? This action cannot be undone.'
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {translate('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Manager Dialog */}
      {inventoryForQR && (
        <InventoryQRManager
          inventory={inventoryForQR}
          onClose={() => {
            setQrManagerOpen(false);
            setInventoryForQR(null);
          }}
        />
      )}

      {/* QR Scanner Dialog */}
      <InventoryQRScannerDialog
        open={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        onSuccess={handleQRScanSuccess}
        onError={handleQRScanError}
      />

      {/* Stock Update Dialog */}
      <InventoryStockUpdateDialog
        open={stockUpdateDialogOpen}
        onClose={() => {
          setStockUpdateDialogOpen(false);
          setScannedInventory(null);
        }}
        inventory={scannedInventory}
        onSuccess={handleStockUpdateSuccess}
      />
    </div>
  );
};
